/*
 * "Attach as ZIP" picker. Runs in an Office dialog: the user chooses files,
 * this page compresses them, and the archive is streamed back to the command
 * that opened the dialog (see ../common/dialogProtocol.ts).
 */

/* global Office, document, window, FileReader, File, FileList, DragEvent, HTMLElement, HTMLInputElement, HTMLButtonElement, setTimeout, clearTimeout */

import {
  ACK_PROBE_TIMEOUT_MS,
  ACK_TIMEOUT_MS,
  CHUNK_SIZE,
  ChildMessage,
  ParentMessage,
  decode,
  encode,
} from "../common/dialogProtocol";
import {
  MAX_BASE64_LENGTH,
  ZipInput,
  base64Bytes,
  createZipBase64,
  formatBytes,
  sanitizeFileName,
} from "../common/zip";
import { language, s as t, setLanguage } from "../common/strings";

const selected: File[] = [];
let busy = false;
let pendingAck: { index: number; resolve: (received: boolean) => void; timer: number } | undefined;

Office.onReady(() => {
  // The command that opened this dialog passes the resolved language along.
  setLanguage(new window.URLSearchParams(window.location.search).get("lang") || undefined);
  applyStaticStrings();
  listenForAcks();

  element<HTMLInputElement>("file-input").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    addFiles(input.files);
    // Reset so picking the same file twice in a row still fires "change".
    input.value = "";
  });

  const dropZone = element<HTMLElement>("drop-zone");
  ["dragenter", "dragover"].forEach((name) =>
    dropZone.addEventListener(name, (event) => {
      event.preventDefault();
      dropZone.classList.add("is-over");
    })
  );
  ["dragleave", "drop"].forEach((name) =>
    dropZone.addEventListener(name, () => dropZone.classList.remove("is-over"))
  );
  dropZone.addEventListener("drop", (event) => {
    event.preventDefault();
    addFiles((event as DragEvent).dataTransfer ? (event as DragEvent).dataTransfer!.files : null);
  });
  // Stop the host from navigating away when a file lands outside the zone.
  window.addEventListener("dragover", (event) => event.preventDefault());
  window.addEventListener("drop", (event) => event.preventDefault());

  element<HTMLButtonElement>("attach").addEventListener("click", () => {
    attach().catch((error: Error) => {
      setBusy(false);
      showError(error.message);
    });
  });

  element<HTMLButtonElement>("cancel").addEventListener("click", () => {
    send({ t: "cancel" });
  });

  render();
});

function addFiles(list: FileList | null): void {
  if (!list || busy) return;
  for (let i = 0; i < list.length; i++) {
    const file = list[i];
    const duplicate = selected.some(
      (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
    );
    if (!duplicate) selected.push(file);
  }
  showError("");
  render();
}

function render(): void {
  const list = element<HTMLElement>("file-list");
  list.innerHTML = "";

  selected.forEach((file, index) => {
    const row = document.createElement("li");

    const name = document.createElement("span");
    name.className = "file-list__name";
    name.textContent = file.name;
    name.title = file.name;

    const size = document.createElement("span");
    size.className = "file-list__size";
    size.textContent = formatBytes(file.size);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "file-list__remove";
    remove.innerHTML = "&times;";
    remove.title = t().removeFile(file.name);
    remove.disabled = busy;
    remove.addEventListener("click", () => {
      selected.splice(index, 1);
      render();
    });

    row.appendChild(name);
    row.appendChild(size);
    row.appendChild(remove);
    list.appendChild(row);
  });

  const total = selected.reduce((sum, file) => sum + file.size, 0);
  element<HTMLElement>("summary").textContent =
    selected.length === 0
      ? t().noFilesSelected
      : t().fileSummary(selected.length, formatBytes(total));

  element<HTMLButtonElement>("attach").disabled = busy || selected.length === 0;
}

async function attach(): Promise<void> {
  if (busy || selected.length === 0) return;
  setBusy(true);
  showError("");

  const inputs: ZipInput[] = [];
  for (let i = 0; i < selected.length; i++) {
    const file = selected[i];
    setProgress((i / selected.length) * 20, t().readingFile(file.name));
    inputs.push({
      name: file.name,
      bytes: await readFile(file),
      date: new Date(file.lastModified),
    });
  }

  const base64 = await createZipBase64(inputs, (percent) =>
    setProgress(20 + percent * 0.6, t().compressingPercent(Math.round(percent)))
  );

  if (base64.length > MAX_BASE64_LENGTH) {
    setBusy(false);
    showError(
      t().archiveTooLarge(
        formatBytes(base64Bytes(base64)),
        formatBytes((MAX_BASE64_LENGTH * 3) / 4)
      )
    );
    return;
  }

  await transfer(zipFileName(), base64);
  setProgress(100, t().done);
}

function zipFileName(): string {
  const raw = element<HTMLInputElement>("zip-name").value.replace(/\.zip$/i, "");
  return sanitizeFileName(raw, t().defaultArchiveName) + ".zip";
}

function readFile(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error(t().couldNotReadFile(file.name)));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Sends the archive to the parent in chunks. The parent acknowledges each one;
 * if no ack arrives for the first chunk the host does not support messageChild,
 * so the rest is sent unpaced instead.
 */
async function transfer(name: string, base64: string): Promise<void> {
  const total = Math.ceil(base64.length / CHUNK_SIZE);
  send({ t: "begin", name, total, length: base64.length });

  let acked = true;
  for (let i = 0; i < total; i++) {
    send({ t: "chunk", i, d: base64.substr(i * CHUNK_SIZE, CHUNK_SIZE) });

    if (acked) {
      const received = await waitForAck(i, i === 0 ? ACK_PROBE_TIMEOUT_MS : ACK_TIMEOUT_MS);
      if (!received) {
        if (i > 0) throw new Error(t().transferStalled);
        acked = false;
      }
    } else {
      await yieldToHost();
    }

    const percent = Math.round(((i + 1) / total) * 100);
    setProgress(80 + percent * 0.2, t().sendingPercent(percent));
  }

  send({ t: "end", total });
}

function listenForAcks(): void {
  try {
    Office.context.ui.addHandlerAsync(
      Office.EventType.DialogParentMessageReceived,
      (arg: unknown) => {
        const raw = (arg as { message?: string }).message;
        const message = raw ? decode<ParentMessage>(raw) : undefined;
        if (message && message.t === "ack" && pendingAck && message.i === pendingAck.index) {
          const waiter = pendingAck;
          pendingAck = undefined;
          clearTimeout(waiter.timer);
          waiter.resolve(true);
        }
      }
    );
  } catch {
    // Host without messageChild support - the ack probe below will time out.
  }
}

function waitForAck(index: number, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      pendingAck = undefined;
      resolve(false);
    }, timeoutMs);
    pendingAck = { index, resolve, timer };
  });
}

/** Gives the host a moment to drain its message queue. */
function yieldToHost(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function send(message: ChildMessage): void {
  Office.context.ui.messageParent(encode(message));
}

function setBusy(value: boolean): void {
  busy = value;
  element<HTMLElement>("progress").hidden = !value;
  element<HTMLInputElement>("zip-name").disabled = value;
  element<HTMLInputElement>("file-input").disabled = value;
  render();
}

function setProgress(percent: number, text: string): void {
  element<HTMLElement>("progress-fill").style.width = Math.min(100, Math.max(0, percent)) + "%";
  element<HTMLElement>("progress-text").textContent = text;
}

function showError(message: string): void {
  const error = element<HTMLElement>("error");
  error.textContent = message;
  error.hidden = !message;
}

/** The markup ships English copy; swap it for the active language on load. */
function applyStaticStrings(): void {
  document.title = t().dialogTitle;
  document.documentElement.lang = language();
  element<HTMLElement>("drop-title").textContent = t().dropFiles;
  element<HTMLElement>("drop-hint").textContent = t().browseHint;
  element<HTMLElement>("name-label").textContent = t().archiveNameLabel;
  element<HTMLButtonElement>("cancel").textContent = t().cancel;
  element<HTMLButtonElement>("attach").textContent = t().attachZip;
  element<HTMLInputElement>("zip-name").value = t().defaultArchiveName;
}

function element<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}
